"use client";

import { useContext, useEffect, useMemo } from "react";

import { useInfiniteQuery } from "@tanstack/react-query";
import { useSnapshot } from "valtio";
import { rootStore } from "@store/rootStore";
import { QUERY_KEYS } from "@config/constant";
import { OspApiClientContext } from "@features/api/client/OspApiClientProvider";

const PAGE_SIZE = 20;

type IUseHistoryProps = {
  params?: {
    mark?: number;
  };
  initialPageParam?: {
    limit?: number;
    offset?: number;
  };
  enabled?: boolean;
};

export const useHistory = (props?: IUseHistoryProps) => {
  const { chatApi } = useContext(OspApiClientContext);
  const { params = {}, initialPageParam, enabled = true } = props || {};
  const pageSize = initialPageParam?.limit || PAGE_SIZE;
  const {
    isLoading: isLoginLoading,
    isFetching: isLoginFetching,
    isLoggedIn,
  } = useSnapshot(rootStore.login);

  // 优化queryKey构建，只使用稳定的标识符，避免对象引用变化
  const queryKey = useMemo(() => {
    // 构建稳定的queryKey，只使用必要的标识信息
    const paramsKey = JSON.stringify(params); // 序列化params确保一致性
    const initialParamKey = JSON.stringify(initialPageParam); // 序列化初始参数

    return [
      QUERY_KEYS.CHAT_HISTORY,
      isLoggedIn,
      paramsKey,
      initialParamKey,
      enabled,
    ];
  }, [isLoggedIn, params, initialPageParam, enabled]);

  // 缓存initialPageParam，避免每次都创建新对象
  const memoizedInitialPageParam = useMemo(
    () => ({
      limit: PAGE_SIZE,
      offset: 0,
      ...initialPageParam,
    }),
    [initialPageParam],
  );

  const {
    data,
    isLoading,
    isPending,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    error,
  } = useInfiniteQuery({
    queryKey,
    enabled: !!chatApi && !isLoginLoading && !isLoginFetching && enabled,
    initialPageParam: memoizedInitialPageParam,
    queryFn: async ({ pageParam }) => {
      const res = await chatApi?.chats
        .chatsList({
          ...pageParam,
          ...params,
        })
        .catch((error) => {
          return Promise.reject(error);
        });

      if (res?.error) {
        return Promise.reject(res?.error);
      }
      return res?.data;
    },
    getNextPageParam: (lastPage, _, lastPageParam) => {
      // 检查是否有数据
      if (!lastPage?.data?.chats?.length) {
        return undefined;
      }

      // 计算当前页的数据
      const currentPageData = lastPage.data.chats;

      // 如果当前页数据少于页面大小，说明没有更多数据了
      if (currentPageData.length < pageSize) {
        return undefined;
      }

      // 返回下一页的参数
      return {
        ...lastPageParam,
        offset: lastPageParam.offset + pageSize,
      };
    },
    select(data) {
      return data?.pages
        ?.map((page: any) => page?.data?.chats)
        .flat()
        ?.filter((item) => !!item);
    },
  });

  useEffect(() => {}, [chatApi, isLoginLoading, enabled]);

  return {
    data: data || [],
    isLoading,
    isPending,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    error,
  };
};
